import type { DesignThemeTokens } from './types';

/**
 * Light theme tokens aligned with Apple system colors (iOS/macOS).
 * @see https://developer.apple.com/design/human-interface-guidelines/color
 */
export const appleLightTokens: DesignThemeTokens = {
  color: {
    background: {
      primary: '#FFFFFF',
      secondary: '#F2F2F7',
      tertiary: '#FFFFFF',
      inverse: '#1C1C1E',
    },
    surface: {
      base: '#FFFFFF',
      elevated: '#FFFFFF',
      grouped: '#F2F2F7',
      chrome: 'rgba(255, 255, 255, 0.30)',
      modal: 'rgba(255, 255, 255, 0.72)',
      overlay: 'rgba(0, 0, 0, 0.4)',
      overlayModal: 'rgba(0, 0, 0, 0.28)',
    },
    border: {
      hairline: 'rgba(60, 60, 67, 0.12)',
      primary: 'rgba(60, 60, 67, 0.29)',
      secondary: '#C6C6C8',
      opaque: '#C6C6C8',
    },
    text: {
      primary: '#000000',
      secondary: 'rgba(60, 60, 67, 0.6)',
      tertiary: 'rgba(60, 60, 67, 0.3)',
      quaternary: 'rgba(60, 60, 67, 0.18)',
      inverse: '#FFFFFF',
      onAccent: '#FFFFFF',
    },
    fill: {
      primary: 'rgba(120, 120, 128, 0.2)',
      secondary: 'rgba(120, 120, 128, 0.16)',
      tertiary: 'rgba(118, 118, 128, 0.12)',
      quaternary: 'rgba(116, 116, 128, 0.08)',
    },
    link: {
      default: '#007AFF',
      hover: '#0051D5',
    },
    semantic: {
      success: '#34C759',
      successMuted: 'rgba(52, 199, 89, 0.16)',
      warning: '#FF9500',
      warningMuted: 'rgba(255, 149, 0, 0.16)',
      error: '#FF3B30',
      errorMuted: 'rgba(255, 59, 48, 0.16)',
      info: '#007AFF',
      infoMuted: 'rgba(0, 122, 255, 0.16)',
    },
    control: {
      primaryBg: '#D95430',
      primaryText: '#FFFFFF',
      secondaryBg: 'rgba(120, 120, 128, 0.12)',
      secondaryText: '#3C3C43',
      secondaryBorder: 'rgba(60, 60, 67, 0.12)',
      outlineBg: 'transparent',
      outlineText: '#D95430',
      outlineBorder: '#D95430',
    },
    input: {
      bg: 'rgba(120, 120, 128, 0.12)',
      border: 'rgba(60, 60, 67, 0.29)',
      borderFocus: '#D95430',
      text: '#000000',
      placeholder: 'rgba(60, 60, 67, 0.3)',
    },
    icon: {
      primary: 'rgba(60, 60, 67, 0.6)',
      secondary: 'rgba(60, 60, 67, 0.3)',
      inverse: '#FFFFFF',
    },
    interaction: {
      hoverPrimary: 'rgba(217, 84, 48, 0.10)',
      hoverSecondary: 'rgba(120, 120, 128, 0.16)',
      focusRing: 'rgba(0, 122, 255, 0.4)',
      selection: 'rgba(0, 122, 255, 0.2)',
    },
  },
  blur: {
    chrome: '10px',
    chromeSaturate: '180%',
    overlay: '4px',
  },
  radius: {
    control: '8px',
    panel: '12px',
    sheet: '16px',
    pill: '999px',
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    xxl: '32px',
  },
  typography: {
    titleLg: '32px',
    titleMd: '22px',
    titleSm: '17px',
    body: '15px',
    callout: '14px',
    caption: '12px',
    weightRegular: '400',
    weightMedium: '500',
    weightSemibold: '600',
  },
  motion: {
    fast: '150ms',
    normal: '250ms',
    panel: '320ms',
    easingStandard: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
    easingEmphasized: 'cubic-bezier(0.2, 0, 0, 1)',
  },
  shadow: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)',
    md: '0 4px 16px rgba(0, 0, 0, 0.1), 0 2px 6px rgba(0, 0, 0, 0.06)',
    lg: '0 12px 32px rgba(0, 0, 0, 0.14), 0 4px 12px rgba(0, 0, 0, 0.08)',
  },
};
